import { Card, DatePicker, InputNumber, Radio, Select, Skeleton, Space, Typography } from "antd"
import { useContext, useEffect, useReducer } from "react"
import { useWorkareaGroupsQuery } from "../../../../domain/workareas/groupsQueries"
import LabeledSwitch from "../../../common/LabeledSwitch"
import VSpace from "../../../common/VSpace"
import { ItemsContext } from "../../../Items/ItemsRoot"
import JobPlanContext from "../JobPlanContext"
import { Action, reducer } from "./recurringWorkareaReducer"
import dayjs from 'dayjs'
import { useUpdateRecurringScheduleMutation } from "../../../../domain/workareas/workareaQueries"
import { defaultSchedule, RecurringSchedule } from "../../../../domain/recurringSchedule"
import { hasValue } from "../../../../helpers/nullable"
import { useDebouncedCallback } from '../../../../helpers/debounce'

const { Text } = Typography
const { RangePicker } = DatePicker

const getDateRange = (schedule: RecurringSchedule): [dayjs.Dayjs, dayjs.Dayjs] => {
  return [dayjs(schedule.startDate), dayjs(schedule.endDate)]
}

const isDateRangeValid = ({ endType, startDate, endDate }: RecurringSchedule): boolean => {
  if (endType === 'date') {
    return hasValue(startDate) && hasValue(endDate)
  }
  return true
}

const isOccurrencesValid = ({ endType, occurrences }: RecurringSchedule): boolean => {
  return endType != 'occurrences' || hasValue(occurrences)
}

const isValid = (schedule: RecurringSchedule): boolean => isDateRangeValid(schedule) && isOccurrencesValid(schedule)

const EditMode = ({ schedule, dispatch }: { schedule: RecurringSchedule, dispatch: (action: Action) => void }) => {
  const frequencyOptions = [
    { value: 'day', label: 'Day(s)' },
    { value: 'week', label: 'Week(s)' },
    { value: 'month', label: 'Month(s)' },
    { value: 'year', label: 'Year(s)' },
  ]

  return <VSpace>
    <Space>
      <LabeledSwitch label="Recurring" checked={schedule.isRecurring} onChange={() => dispatch({ type: 'IS_RECURRING_CHANGED', value: !schedule.isRecurring })} />
    </Space>
    {schedule.isRecurring && <>
      <Text type="secondary">Frequency</Text>
      <Space>
        Repeat every
        <InputNumber value={schedule.repeatCount} onChange={value => dispatch({ type: 'REPEAT_COUNT_CHANGED', value: value ?? 1 })} min={1} />
        <Select value={schedule.repeatUnits} onChange={value => dispatch({ type: 'REPEAT_UNITS_CHANGED', value })} options={frequencyOptions} popupMatchSelectWidth={false} style={{ width: 105 }} />
      </Space>
      <Text type="secondary">Duration</Text>
      <Radio.Group name="end" value={schedule.endType} onChange={e => dispatch({ type: 'END_TYPE_CHANGED', value: e.target.value })}>
        <VSpace>
          <Radio value="date">
            <Space>
              Date Range
              <RangePicker
                format="MMM DD, YYYY"
                disabled={schedule.endType !== 'date'}
                value={getDateRange(schedule)}
                onChange={date => dispatch({ type: 'DATE_RANGE_CHANGED', value: date as [dayjs.Dayjs, dayjs.Dayjs] })}
                status={isDateRangeValid(schedule) ? undefined : 'error'}
                allowClear={false}
              />
            </Space>

          </Radio>
          <Radio value="occurrences">
            <Space>Repeat
              <InputNumber
                disabled={schedule.endType != 'occurrences'}
                value={schedule.occurrences}
                onChange={value => dispatch({ type: 'OCCURRENCES_CHANGED', value: value ?? 1 })}
                status={isOccurrencesValid(schedule) ? undefined : 'error'}
              />
              time(s)
            </Space>
          </Radio>
        </VSpace>
      </Radio.Group>
    </>}
  </VSpace>
}

const RecurringWorkareaCard = () => {
  const { currentSelection } = useContext(JobPlanContext)
  const { jobData: { jobId } } = useContext(ItemsContext)
  const workareaGroupsQuery = useWorkareaGroupsQuery(jobId)
  const workareaGroups = workareaGroupsQuery.data ?? []
  const workarea = workareaGroups.flatMap(g => g.workareas).find(w => w.id === currentSelection?.workareaId)
  useEffect(() => {
    if (workarea) {
      if (workarea.recurringScheduleConfig) {
        dispatch({ type: 'RESET', value: JSON.parse(workarea.recurringScheduleConfig) as RecurringSchedule ?? defaultSchedule() })
      } else {
        dispatch({ type: 'RESET', value: defaultSchedule() })
      }
    }
  }, [currentSelection?.workareaId])

  const updateScheduleMutation = useUpdateRecurringScheduleMutation()

  const [schedule, dispatch] = useReducer(reducer, { ...defaultSchedule(), hasChanges: false })


  const handleUpdate = useDebouncedCallback((updatedSchedule: RecurringSchedule) => {
    const workareaId = currentSelection?.workareaId
    if (!isValid(updatedSchedule) || workareaId === undefined) {
      return
    }
    updateScheduleMutation.mutate({
      jobId,
      workareaId,
      schedule: updatedSchedule,
    })
  }, 1000, [jobId, currentSelection?.workareaId])

  useEffect(() => {
    if (schedule.hasChanges) {
      handleUpdate(schedule)
    }
  }, [schedule])


  return <Card title="Recurring Workarea">
    {workareaGroupsQuery.isLoading
      ? <Skeleton />
      : <EditMode schedule={schedule} dispatch={dispatch} />
    }
  </Card>
}

export default RecurringWorkareaCard
